/**
 * What automation rules did to one row, folded under the row panel's header:
 * the answer to "why did this cell change?". Collapsed by default, and its
 * history is only read once opened.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ChevronDownIcon, ChevronRightIcon, ZapIcon } from 'lucide-react';

import { trackerQueryOptions } from '@/entities/tracker';
import {
  trackerAutomationsQueryOptions,
  type TrackerAutomationRun,
} from '@/entities/tracker-automation';
import { useCurrentWorkspaceId } from '@/entities/workspace';

import { summarize } from '../../lib/automation-model';
import { useAutomationBoard } from '../../lib/useAutomationBoard';
import { useFilterOptionLabels } from '../../lib/useFilterOptionLabels';
import { AutomationRunList } from '../AutomationsPanel/AutomationRunList';

export function RowAutomationActivity({
  trackerId,
  rowKey,
}: {
  trackerId: string;
  rowKey: string;
}) {
  const { t } = useTranslation('trackers');
  const [open, setOpen] = useState(false);
  const workspaceId = useCurrentWorkspaceId() ?? '';
  const { data: tracker } = useQuery({
    ...trackerQueryOptions(workspaceId, trackerId),
    enabled: open && Boolean(workspaceId),
  });
  const { data: rules = [] } = useQuery({
    ...trackerAutomationsQueryOptions(trackerId),
    enabled: open,
  });
  const { columns, triggerColumns, customGroups } = useAutomationBoard(tracker);
  const labels = useFilterOptionLabels();

  const ruleLabel = (run: TrackerAutomationRun) => {
    if (run.automationName) return run.automationName;
    const rule = rules.find((r) => r.id === run.automationId);
    if (!rule) return t('trackersBoard.automationRuns.aDeletedAutomation');
    return summarize(
      rule.trigger,
      rule.actions,
      triggerColumns,
      customGroups,
      rule.conditions,
      labels,
      rule.timeZone ?? 'UTC',
    );
  };

  return (
    <section className="border-b border-border">
      <button
        type="button"
        className="flex w-full items-center gap-2 px-4 py-2 text-sm font-medium text-foreground hover:bg-accent/40"
        aria-expanded={open}
        onClick={() => setOpen((on) => !on)}
      >
        {open ? (
          <ChevronDownIcon className="size-4" aria-hidden />
        ) : (
          <ChevronRightIcon className="size-4" aria-hidden />
        )}
        <ZapIcon className="size-4 text-muted-foreground" aria-hidden />
        {t('trackersBoard.automationRuns.rowActivity')}
      </button>
      {open ? (
        <div className="max-h-72 overflow-y-auto px-4 pb-3">
          <AutomationRunList
            trackerId={trackerId}
            filter={{ rowRef: rowKey }}
            columns={columns}
            groups={customGroups}
            ruleLabel={ruleLabel}
          />
        </div>
      ) : null}
    </section>
  );
}
